import * as React from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * A compact calendar-toolbar pill that toggles a flat popover menu anchored beneath it. One shared
 * primitive for the builder toolbars (duration / interval / fill-from-type) in both the ad-hoc and
 * rolling booking builders: a bordered pill button that opens a positioned menu, closes on
 * outside-click or Esc, and — matching the rest of the app — respects prefers-reduced-motion (the
 * open animation is gated behind {@code motion-safe}).
 *
 * Pass menu content as a node, or as a render function that receives a {@code close} callback so an
 * action inside the menu can dismiss it (e.g. after applying a fill).
 */
export function ToolbarPopover({
  label,
  title,
  ariaLabel,
  align = "start",
  active,
  className,
  menuClassName,
  children,
}: {
  /** The pill's visible content (icon + current value). */
  label: React.ReactNode;
  title?: string;
  ariaLabel?: string;
  /** Which edge of the pill the menu aligns to. */
  align?: "start" | "end" | "center";
  /** Draw the pill in its "set"/armed state (a value chosen). */
  active?: boolean;
  className?: string;
  /** Sizes the menu to its content — pass e.g. "w-72" for a contextual width. */
  menuClassName?: string;
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);
  const close = React.useCallback(() => setOpen(false), []);

  // Close on an outside click or Esc — the same lightweight pattern the meeting-location picker uses.
  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        type="button"
        title={title}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex items-center gap-1 rounded-full border bg-background px-2.5 py-1 text-xs font-medium text-foreground transition hover:border-teal-400 hover:bg-muted",
          open || active ? "border-teal-400" : "border-border",
          className,
        )}
      >
        {label}
        <ChevronDown
          className={cn(
            "h-3 w-3 shrink-0 text-muted-foreground transition motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <div
          role="dialog"
          className={cn(
            "absolute top-[calc(100%+6px)] z-30 rounded-lg border border-border bg-popover p-3 text-left text-popover-foreground shadow-lg",
            "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95 motion-safe:duration-100",
            align === "end"
              ? "right-0 origin-top-right"
              : align === "center"
                ? "left-1/2 -translate-x-1/2 origin-top"
                : "left-0 origin-top-left",
            menuClassName,
          )}
        >
          {typeof children === "function" ? children(close) : children}
        </div>
      )}
    </div>
  );
}
